import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Clock,
  Coins,
  ExternalLink,
  FileText,
  Gavel,
  Image as ImageIcon,
  Pencil,
  ScanEye,
  Star,
  StopCircle,
  Trash2,
  TrendingDown,
  XCircle,
  Check,
  X,
  Upload,
  Loader2,
  Eye,
} from "lucide-react";
import { AuctionOverview } from "@forhandlerborsen/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { formatMoney } from "../../lib/formatters";
import { AuctionStatusBadge, LineStatusBadge } from "../../components/StatusBadges";
import { NegotiationModal } from "../../components/NegotiationModal";
import { AuctionStatsGrid } from "../../components/AuctionStatsGrid";
import { AuctionTimelineCard } from "../../components/AuctionTimelineCard";
import { AuctionPickupCard } from "../../components/AuctionPickupCard";
import { AuctionStatusActions } from "../../components/AuctionStatusActions";
import { AuctionBidHistory } from "../../components/AuctionBidHistory";
import { AuctionAutoBids, type AutoBidRow } from "../../components/AuctionAutoBids";
import { useAdminAuth } from "../../lib/use-admin-auth";
import { AuctionDocumentsTab, type GeneralDocument } from "../../components/documents/AuctionDocumentsTab";
import { ConditionReportTab } from "../../components/documents/ConditionReportTab";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { SupportTicketsPanel, type LinkedSupportTicket } from "../../components/SupportTicketsPanel";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

function getAccountingDocumentLabel(documentType: string, lowercase = false) {
  if (documentType === "credit_note") return lowercase ? "kreditnota" : "Kreditnota";
  if (documentType === "purchase_settlement") return lowercase ? "kjøpsoppgjør" : "Kjøpsoppgjør";
  return lowercase ? "faktura" : "Faktura";
}

type VehicleImage = { id: string; url: string; thumbnailUrl: string | null; isPrimary: boolean; sortOrder: number; altText: string | null };
type BidAuditMeta = {
  audit?: {
    bidder?: {
      id?: string | null;
      email?: string | null;
      role?: string | null;
      name?: string | null;
      phoneNumber?: string | null;
      emailVerified?: boolean | null;
      phoneNumberVerified?: boolean | null;
      registeredAt?: string | null;
    };
    organization?: {
      id?: string | null;
      slug?: string | null;
      name?: string | null;
    };
    request?: {
      capturedAt?: string | null;
      ipAddress?: string | null;
      forwardedFor?: string | null;
      forwarded?: string | null;
      userAgent?: string | null;
      acceptLanguage?: string | null;
      referer?: string | null;
      origin?: string | null;
      host?: string | null;
      secChUa?: string | null;
      secChUaMobile?: string | null;
      secChUaPlatform?: string | null;
      secFetchSite?: string | null;
      secFetchMode?: string | null;
      secFetchDest?: string | null;
      cfCountry?: string | null;
      cfCity?: string | null;
      cfRegion?: string | null;
      cfLatitude?: string | null;
      cfLongitude?: string | null;
      organizationSlug?: string | null;
    };
  };
  isAutoBid?: boolean;
  triggeredByBidderOrganizationId?: string | null;
};
type BidRow = {
  id: string;
  amount: number;
  status: string;
  source: string;
  meta: BidAuditMeta | null;
  createdAt: string;
  bidderUserId: string;
  bidderName: string;
  bidderEmail: string | null;
  bidderPhoneNumber?: string | null;
  bidderOrganizationId: string;
  bidderOrgName: string;
  bidderOrgSlug?: string | null;
};
type Vehicle = {
  id: string; make: string; model: string; variant: string | null; modelYear: number | null;
  mileage: number | null; registrationNumber: string | null; vin: string | null;
  fuelType: string | null; transmission: string | null; bodyType: string | null;
  wheelDrive: string | null; exteriorColor: string | null; interiorColor: string | null;
  nextEuControl: string | null;
  conditionGrade: string | null; conditionSummary: string | null;
  technicalData: any;
  images: VehicleImage[];
};
type AuctionOffer = {
  id: string; round: number; offeredBy: string; amount: number; notes: string | null;
  status: string; createdAt: string; createdByUserId: string | null;
};
type AccountingDocumentSummary = {
  id: string;
  documentNumber: string | null;
  documentType: string;
  status: string;
  totalAmount: number;
  outstandingAmount: number | null;
  issueDate: string | null;
  dueDate: string | null;
  paidAt: string | null;
  documentUrl: string | null;
  createdAt: string;
};
type AccountingOrderLine = {
  id: string;
  description: string;
  lineType: string;
  direction: string;
  status: string;
  quantity: number;
  unitPriceAmount: number;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  currency: string;
  externalReference: string | null;
  createdAt: string;
  invoicedAt: string | null;
  document: {
    id: string;
    documentNumber: string | null;
    documentType: string;
    status: string;
    documentUrl: string | null;
  } | null;
};
type AuctionEventRow = {
  id: string;
  type: string;
  title: string;
  visibility: string;
  actorType: string;
  actorUserId: string | null;
  actorOrganizationId: string | null;
  source: string;
  statusFrom: string | null;
  statusTo: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actorUser: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
  actorOrganization: {
    id: string;
    name: string;
    slug: string;
  } | null;
};
type AuctionDetail = {
  id: string; referenceNumber: string; title: string; description: string | null; status: string;
  pickupAddress: string | null; pickupPostalCode: string | null; pickupCity: string | null;
  visibility: string; startingBid: number; reservePrice: number | null; buyNowPrice: number | null;
  minimumIncrement: number; currentBid: number; spread: number; spreadLocked: boolean; bidCount: number;
  startsAt: string | null; endsAt: string | null; publishedAt: string | null;
  type?: "regular" | "flash" | null; durationMinutes?: number | null;
  closedAt: string | null; submittedAt: string | null; createdAt: string; updatedAt: string;
  organization: { id: string; name: string; slug: string };
  vehicle: Vehicle | null;
  bids: BidRow[];
  autoBids: AutoBidRow[];
  events: AuctionEventRow[];
  offers: AuctionOffer[];
  orderLines: AccountingOrderLine[];
  documents: AccountingDocumentSummary[];
  generalDocuments: GeneralDocument[];
  supportTickets: LinkedSupportTicket[];
  contractsEnabled?: boolean;
};

function formatEventActor(event: AuctionEventRow) {
  if (event.actorUser?.name) {
    return event.actorOrganization?.name
      ? `${event.actorUser.name} · ${event.actorOrganization.name}`
      : event.actorUser.name;
  }

  if (event.actorOrganization?.name) {
    return event.actorOrganization.name;
  }

  if (event.actorType === "worker") {
    return "Worker";
  }

  if (event.actorType === "system") {
    return "System";
  }

  if (event.actorType === "api_key") {
    return "API key";
  }

  return "Ukjent";
}

function formatEventMetadata(metadata: Record<string, unknown> | null) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return "—";
  }

  const compact = JSON.stringify(metadata);
  return compact.length > 160 ? `${compact.slice(0, 157)}...` : compact;
}

export function AuksjonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAdminAuth();
  const isDeveloper = profile?.superAdminRole === "developer";
  const [data, setData] = useState<AuctionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [selectedBidAudit, setSelectedBidAudit] = useState<BidRow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "oversikt";
  const setActiveTab = (tab: string) => setSearchParams({ tab }, { replace: true });
  const [spreadOverride, setSpreadOverride] = useState<string>("");
  const [savingSpread, setSavingSpread] = useState(false);
  const [sendingPurchaseContract, setSendingPurchaseContract] = useState(false);
  const [purchaseContractError, setPurchaseContractError] = useState<string | null>(null);
  const [purchaseContractSuccess, setPurchaseContractSuccess] = useState<string | null>(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSuccess, setInvoiceSuccess] = useState<string | null>(null);
  
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");

  type ViewRow = {
    id: string;
    userId: string;
    organizationId: string | null;
    source: string;
    viewedAt: string;
    userName: string | null;
    userEmail: string | null;
    orgName: string | null;
    orgSlug: string | null;
  };
  const [views, setViews] = useState<ViewRow[]>([]);
  const [viewsLoaded, setViewsLoaded] = useState(false);
  const [viewsUniqueOnly, setViewsUniqueOnly] = useState(false);

  useEffect(() => {
    if (activeTab === "visninger" && !viewsLoaded && id) {
      fetch(`${API}/api/admin/auctions/${id}/views`, { credentials: "include" })
        .then((r) => r.json())
        .then((d) => { setViews(Array.isArray(d) ? d : []); setViewsLoaded(true); })
        .catch(() => setViewsLoaded(true));
    }
  }, [activeTab, viewsLoaded, id]);

  const getOrderLineAccountingStatus = (line: AccountingOrderLine) => {
    if (!line.document) {
      return <Badge variant="muted">Ikke fakturert</Badge>;
    }

    if (line.document.status === "paid") {
      return <Badge variant="success">Betalt</Badge>;
    }

    return <Badge variant="accent">Fakturert</Badge>;
  };

  const loadAuction = useCallback(async () => {
    if (!id) return;
    const response = await fetch(`${API}/api/admin/auctions/${id}`, { credentials: "include" });
    const nextData = response.ok ? await response.json() as AuctionDetail : null;
    setData(nextData);
    setLoading(false);
    if (nextData) setTitleValue(nextData.title);
  }, [id]);

  useEffect(() => {
    void loadAuction();
  }, [loadAuction]);

  useEffect(() => {
    if (!id) return;

    const sse = new EventSource(`${API}/api/auctions/${id}/events`, { withCredentials: true });
    sse.addEventListener("auction", () => {
      void loadAuction();
    });

    return () => sse.close();
  }, [id, loadAuction]);

  async function parseActionError(response: Response) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    const message = payload?.error ?? `Request failed (${response.status})`;
    alert(message);
    return null;
  }

  async function post(path: string, body?: object) {
    const r = await fetch(`${API}/api/admin/auctions/${id}${path}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    return r.ok ? r.json() : parseActionError(r);
  }

  async function patch(path: string, body: object) {
    const r = await fetch(`${API}/api/admin/auctions/${id}${path}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.ok ? r.json() : parseActionError(r);
  }

  async function del(url: string) {
    const r = await fetch(`${API}${url}`, { method: "DELETE", credentials: "include" });
    return r.ok;
  }

  if (loading) return <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto"><p className="text-sm text-muted-foreground">Laster auksjon...</p></div>;
  if (!data) return <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto"><p className="text-sm text-destructive">Auksjonen ble ikke funnet.</p></div>;

  const isPendingReview = data.status === "pending_review";
  const isLive = data.status === "live";
  const isPaused = data.status === "paused";
  const isClosed = ["sold", "no_sale", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"].includes(data.status);
  const isPendingSellerDecision = data.status === "pending_seller_decision";
  const isNegotiating = data.status === "negotiating";
  const isNoBids = data.status === "no_bids";
  const isSold = data.status === "sold";
  const canStartAuction = ["approved", "scheduled", "paused", "no_bids"].includes(data.status);
  const canDeleteAuction = ["draft", "pending_review", "changes_requested", "no_bids"].includes(data.status);
  const startActionLabel =
    isPaused ? "Fortsett auksjon" : isClosed ? "Restart auksjon" : isNoBids ? "Relansér auksjon" : "Start auksjon";

  async function deleteAuction() {
    if (!data || !canDeleteAuction || deleting) return;
    const msg = isNoBids
      ? "Skjule denne auksjonen? Den vil bli fjernet fra alle lister, men dataene beholdes i databasen."
      : "Slette denne auksjonen? Dette fjerner også tilknyttet kjøretøy, bilder og eventuelle oppgaver.";
    if (!confirm(msg)) return;

    setDeleting(true);
    const ok = await del(`/api/admin/auctions/${data.id}`);
    setDeleting(false);

    if (!ok) {
      alert("Auksjonen kunne ikke slettes.");
      return;
    }

    navigate("/auksjoner");
  }

  async function handleDeleteBid(bid: BidRow) {
    if (!data) return;
    const ok = await del(`/api/admin/bids/${bid.id}`);
    if (ok) {
      const remaining = data.bids.filter(x => x.id !== bid.id);
      const newHighest = remaining[0]?.amount ?? 0;
      setData(d => d ? { ...d, bids: remaining, currentBid: newHighest, bidCount: remaining.length } : d);
    }
  }

  async function sendPurchaseContract() {
    if (!data || sendingPurchaseContract) return;

    setSendingPurchaseContract(true);
    setPurchaseContractError(null);
    setPurchaseContractSuccess(null);

    try {
      const response = await fetch(
        `${API}/api/organizations/${data.organization.slug}/auctions/${data.id}/send-purchase-contract`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      const payload = await response.json() as {
        error?: string;
        reusedExistingDocument?: boolean;
        status?: string;
      };

      if (!response.ok) {
        setPurchaseContractError(payload.error ?? "Kunne ikke sende kjøpskontrakt");
        return;
      }

      setPurchaseContractSuccess(
        payload.status === "render_queued"
          ? "Kjøpskontrakten er lagt i kø for generering og utsending til kjøper."
          : payload.status === "signing_queued"
            ? "Eksisterende kjøpskontrakt er lagt i kø for signering."
            : payload.status === "awaiting_signature"
              ? "Kjøpskontrakten finnes allerede og venter på signering."
              : payload.status === "completed"
                ? "Kjøpskontrakten er allerede signert."
                : "Kjøpskontrakten finnes allerede for denne handelen. Se dokumentstatus i dokumentoversikten.",
      );
    } catch {
      setPurchaseContractError("Kunne ikke sende kjøpskontrakt");
    } finally {
      setSendingPurchaseContract(false);
    }
  }

  async function sendInvoice() {
    if (!data || sendingInvoice) return;

    setSendingInvoice(true);
    setInvoiceError(null);
    setInvoiceSuccess(null);

    try {
      const response = await fetch(
        `${API}/api/admin/auctions/${data.id}/send-invoice`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      const payload = await response.json() as {
        error?: string;
        status?: string;
        orderLineCount?: number;
      };

      if (!response.ok) {
        setInvoiceError(payload.error ?? "Kunne ikke sende faktura");
        return;
      }

      setInvoiceSuccess(
        payload.status === "queued"
          ? `Faktura er lagt i kø for opprettelse og utsending i Fiken${payload.orderLineCount ? ` (${payload.orderLineCount} linjer)` : ""}.`
          : "Fakturaen er sendt til Fiken.",
      );
    } catch {
      setInvoiceError("Kunne ikke sende faktura");
    } finally {
      setSendingInvoice(false);
    }
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/auksjoner")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0 group relative">
          <div className="flex items-center gap-3 flex-wrap">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <Input 
                  value={titleValue} 
                  onChange={(e) => setTitleValue(e.target.value)} 
                  className="text-2xl font-black h-9 py-0" 
                  autoFocus
                  onKeyDown={async (e) => {
                    if (e.key === "Enter") {
                      await patch("", { title: titleValue });
                      setEditingTitle(false);
                    }
                    if (e.key === "Escape") {
                      setTitleValue(data.title);
                      setEditingTitle(false);
                    }
                  }}
                />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={async () => {
                  await patch("", { title: titleValue });
                  setEditingTitle(false);
                }}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => {
                  setTitleValue(data.title);
                  setEditingTitle(false);
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{data.title}</h1>
                <button 
                  onClick={() => setEditingTitle(true)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-muted transition-all text-muted-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <AuctionStatusBadge status={data.status} />
            {data.type === "flash" && (
              <Badge variant="accent" className="font-black uppercase tracking-wider">Lynauksjon</Badge>
            )}
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border">#{data.referenceNumber}</span>
              {data.vehicle?.registrationNumber && (
                <span className="rounded-md bg-accent/10 px-2 py-0.5 text-xs font-mono font-bold text-accent border border-accent/20">
                  {data.vehicle.registrationNumber}
                </span>
              )}
              {data.vehicle?.vin && (
                <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-mono text-muted-foreground border border-border">
                  VIN: {data.vehicle.vin}
                </span>
              )}
            </div>
          </div>
          <button className="text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => navigate(`/organisasjoner/${data.organization.id}`)}>
            {data.organization.name}
          </button>
        </div>
        <div className="flex items-center gap-2 pt-1">
          {(isPendingSellerDecision || isNegotiating) && (
            <Button
              className="gap-2 px-4 shadow-sm"
              onClick={() => setNegotiationOpen(true)}
            >
              <TrendingDown className="h-4 w-4" />
              Forhandle
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="bilder">Bilder ({data.vehicle?.images.length ?? 0})</TabsTrigger>
          <TabsTrigger value="tilstandsrapport">Tilstandsrapport</TabsTrigger>
          <TabsTrigger value="bud">Bud ({data.bids.length})</TabsTrigger>
          <TabsTrigger value="hendelser">Hendelser ({data.events.length})</TabsTrigger>
          <TabsTrigger value="regnskap">Regnskap ({data.orderLines.length})</TabsTrigger>
          <TabsTrigger value="dokumenter">Dokumenter ({(data.documents.length ?? 0) + (data.generalDocuments?.length ?? 0)})</TabsTrigger>
          <TabsTrigger value="support">Support ({data.supportTickets.length})</TabsTrigger>
          <TabsTrigger value="visninger">Visninger</TabsTrigger>
          {isDeveloper && <TabsTrigger value="rediger">Rediger</TabsTrigger>}
        </TabsList>

        {/* ─── OVERSIKT ─── */}
        <TabsContent value="oversikt" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <AuctionStatsGrid
                currentBid={data.currentBid}
                bidCount={data.bidCount}
                startingBid={data.startingBid}
                reservePrice={data.reservePrice}
                status={data.status}
                endsAt={data.endsAt}
                spread={data.spread}
                spreadLocked={data.spreadLocked}
                uniqueBidders={new Set(data.bids.map(b => b.bidderUserId)).size}
                type={data.type}
                durationMinutes={data.durationMinutes}
                onSpreadUpdate={async (newSpread: number) => {
                  const r = await fetch(`${API}/api/admin/auctions/${data.id}/spread`, {
                    method: "PATCH",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ spread: newSpread }),
                  });
                  if (r.ok) {
                    setData(d => d ? { ...d, spread: newSpread, spreadLocked: true } : d);
                  }
                }}
                onSpreadReset={async () => {
                  const r = await fetch(`${API}/api/admin/auctions/${data.id}/spread`, {
                    method: "PATCH",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ auto: true }),
                  });
                  if (r.ok) {
                    const j = await r.json();
                    setData(d => d ? { ...d, spread: j.spread, spreadLocked: false } : d);
                  }
                }}
              />

              <AuctionOverview
                title={data.title}
                images={(data.vehicle?.images ?? []).map(img => ({ url: img.url, thumbnailUrl: img.thumbnailUrl, altText: img.altText }))}
                vehicle={data.vehicle ? {
                  ...data.vehicle,
                  keys: data.vehicle.technicalData?.numberOfKeys,
                  tires: data.vehicle.technicalData?.numberOfWheelSets,
                  tireCond: data.vehicle.technicalData?.tireCondition,
                  service: data.vehicle.technicalData?.serviceHistory,
                } : null}
                description={data.description}
                onUpdateTitle={async (title) => {
                  await patch("", { title });
                  setData(d => d ? { ...d, title } : d);
                }}
                onUpdateDescription={async (description) => {
                  await patch("", { description });
                  setData(d => d ? { ...d, description } : d);
                }}
                onUpdateVehicle={async (fields) => {
                  const technicalData = { ...(data.vehicle?.technicalData || {}) };
                  const payload: any = { ...fields };
                  
                  if (fields.keys !== undefined) {
                    technicalData.numberOfKeys = fields.keys;
                    delete payload.keys;
                  }
                  if (fields.tires !== undefined) {
                    technicalData.numberOfWheelSets = fields.tires;
                    delete payload.tires;
                  }
                  if (fields.tireCond !== undefined) {
                    technicalData.tireCondition = fields.tireCond;
                    delete payload.tireCond;
                  }
                  if (fields.service !== undefined) {
                    technicalData.serviceHistory = fields.service;
                    delete payload.service;
                  }
                  
                  if (Object.keys(technicalData).length > 0) {
                    payload.technicalData = technicalData;
                  }

                  await patch("/vehicle", payload);
                  setData(d => d ? { ...d, vehicle: d.vehicle ? { ...d.vehicle, ...payload } as Vehicle : null } : d);
                }}
                showAllForAdmin={true}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AuctionTimelineCard
                  createdAt={data.createdAt}
                  submittedAt={data.submittedAt}
                  publishedAt={data.publishedAt}
                  startsAt={data.startsAt}
                  endsAt={data.endsAt}
                  closedAt={data.closedAt}
                />
                <AuctionPickupCard
                  address={data.pickupAddress}
                  postalCode={data.pickupPostalCode}
                  city={data.pickupCity}
                  onUpdate={async (fields) => {
                    await patch("", fields);
                    setData(d => d ? { ...d, ...fields } : d);
                  }}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <AuctionStatusActions
                status={data.status}
                isPendingReview={isPendingReview}
                isLive={isLive}
                canStartAuction={canStartAuction}
                isPendingSellerDecision={isPendingSellerDecision}
                isNoBids={isNoBids}
                isSold={isSold}
                canDeleteAuction={canDeleteAuction}
                pausing={pausing}
                starting={starting}
                deleting={deleting}
                startActionLabel={startActionLabel}
                contractsEnabled={data.contractsEnabled ?? false}
                sendingPurchaseContract={sendingPurchaseContract}
                purchaseContractSuccess={purchaseContractSuccess}
                purchaseContractError={purchaseContractError}
                sendingInvoice={sendingInvoice}
                invoiceSuccess={invoiceSuccess}
                invoiceError={invoiceError}
                onPost={post}
                onPatch={patch}
                onDelete={deleteAuction}
                onSendPurchaseContract={sendPurchaseContract}
                onSendInvoice={sendInvoice}
                onOpenNegotiation={() => setNegotiationOpen(true)}
                onUpdateData={(fields) => setData(d => d ? { ...d, ...fields } : d)}
                type={data.type}
                durationMinutes={data.durationMinutes}
              />

              <AuctionBidHistory
                bids={data.bids}
                onAudit={(bid) => setSelectedBidAudit(bid)}
              />

              <AuctionAutoBids
                autoBids={data.autoBids ?? []}
                leadingOrganizationId={data.bids[0]?.bidderOrganizationId ?? null}
              />

            </div>
          </div>
        </TabsContent>

        {/* ─── BILDER ─── */}
        <TabsContent value="bilder" className="mt-6">
          <BilderTab
            images={data.vehicle?.images ?? []}
            onDelete={async (imgId) => {
              const ok = await del(`/api/admin/images/${imgId}`);
              if (ok) setData(d => d ? { ...d, vehicle: d.vehicle ? { ...d.vehicle, images: d.vehicle.images.filter(i => i.id !== imgId) } : null } : d);
            }}
            onRerunBlur={async (imgId) => {
              const r = await fetch(`${API}/api/admin/images/${imgId}/blur`, {
                method: "POST",
                credentials: "include",
              });
              if (!r.ok) {
                const e = await r.json().catch(() => null);
                alert(e?.error ?? "Kunne ikke starte sladding");
                throw new Error("Blur re-run failed");
              }
            }}
            onReorder={async (ordered) => {
              await fetch(`${API}/api/admin/auctions/${data.id}/images/reorder`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order: ordered.map(img => ({ id: img.id, sortOrder: img.sortOrder })) }),
              });
              setData(d => d ? { ...d, vehicle: d.vehicle ? { ...d.vehicle, images: ordered } : null } : d);
            }}
            onUpload={async (file) => {
              const form = new FormData();
              form.append("file", file);
              const r = await fetch(`${API}/api/admin/auctions/${data.id}/images/upload`, {
                method: "POST",
                credentials: "include",
                body: form,
              });
              if (!r.ok) { const e = await r.json().catch(() => null); alert(e?.error ?? "Opplasting feilet"); return; }
              const img: VehicleImage = await r.json();
              setData(d => d ? { ...d, vehicle: d.vehicle ? { ...d.vehicle, images: [...d.vehicle.images, img] } : null } : d);
            }}
          />
        </TabsContent>

        {/* ─── TILSTANDSRAPPORT ─── */}
        <TabsContent value="tilstandsrapport" className="mt-6">
          <ConditionReportTab vehicleId={data.vehicle?.id ?? null} />
        </TabsContent>

        <TabsContent value="regnskap" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Ordrelinjer på auksjonen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.orderLines.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 text-muted-foreground gap-3">
                    <Coins className="h-9 w-9 opacity-25" />
                    <p className="text-sm">Ingen ordrelinjer er opprettet ennå.</p>
                  </div>
                ) : (
                  data.orderLines.map((line) => (
                    <div key={line.id} className="rounded-2xl border border-border bg-muted/20 px-4 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm">{line.description}</p>
                            {getOrderLineAccountingStatus(line)}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-black">
                            <span>{line.lineType}</span>
                            <span>·</span>
                            <span>{line.direction}</span>
                            {line.externalReference && (
                              <>
                                <span>·</span>
                                <span>{line.externalReference}</span>
                              </>
                            )}
                          </div>
                          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                            <p>Opprettet {formatDateTime(line.createdAt)}</p>
                            {line.invoicedAt && <p>Fakturert {formatDateTime(line.invoicedAt)}</p>}
                            {line.document && (
                              <p className="flex items-center gap-1.5">
                                Koblet til
                                <button
                                  className="font-medium text-foreground hover:text-primary transition-colors"
                                  onClick={() => navigate(`/regnskap/${line.document!.id}`)}
                                >
                                  {getAccountingDocumentLabel(line.document.documentType, true)} {line.document.documentNumber ?? "utkast"}
                                </button>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base font-black">{formatMoney(line.totalAmount, line.currency)}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {line.quantity} × {formatMoney(line.unitPriceAmount, line.currency)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Regnskapsdokumenter</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ingen dokumenter er opprettet for denne auksjonen ennå.</p>
                ) : (
                  data.documents.map((doc) => (
                    <div key={doc.id} className="rounded-2xl border border-border px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm">
                              {getAccountingDocumentLabel(doc.documentType)} {doc.documentNumber ?? "utkast"}
                            </p>
                            <LineStatusBadge status={doc.status} />
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                            {doc.issueDate && <p>Utstedt {formatDateTime(doc.issueDate)}</p>}
                            {doc.dueDate && <p>Forfall {formatDateTime(doc.dueDate)}</p>}
                            {doc.paidAt && <p>Betalt {formatDateTime(doc.paidAt)}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-sm">{formatMoney(doc.totalAmount)}</p>
                          {doc.outstandingAmount != null && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Utestående {formatMoney(doc.outstandingAmount)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/regnskap/${doc.id}`)}>
                          Åpne
                        </Button>
                        {doc.documentUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => window.open(doc.documentUrl!, "_blank", "noopener,noreferrer")}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            PDF
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="dokumenter">
          <AuctionDocumentsTab
            documents={data.documents}
            generalDocuments={data.generalDocuments ?? []}
          />
        </TabsContent>

        <TabsContent value="support" className="mt-6">
          <SupportTicketsPanel
            tickets={data.supportTickets}
            emptyLabel="Ingen supporthenvendelser er koblet til denne auksjonen."
          />
        </TabsContent>

        {/* ─── BUD ─── */}
        <TabsContent value="bud" className="mt-6 space-y-6">
          <div className="max-w-md">
            <AuctionAutoBids
              autoBids={data.autoBids ?? []}
              leadingOrganizationId={data.bids[0]?.bidderOrganizationId ?? null}
            />
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">#</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Budgiver</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Organisasjon</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Beløp</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Tidspunkt</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.bids.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Ingen bud ennå</td></tr>
                ) : (
                  data.bids.map((b, i) => (
                    <tr key={b.id} className={i === 0 ? "bg-green-500/5" : ""}>
                      <td className="px-6 py-3 text-muted-foreground font-mono text-xs">{i + 1}</td>
                      <td className="px-6 py-3">
                        <p className="font-medium">{b.bidderName}</p>
                        {b.bidderEmail && <p className="text-xs text-muted-foreground">{b.bidderEmail}</p>}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground hidden sm:table-cell">
                        <p>{b.bidderOrgName}</p>
                        {b.bidderOrgSlug && <p className="text-xs">{b.bidderOrgSlug}</p>}
                      </td>
                      <td className="px-6 py-3 text-right font-semibold">
                        {b.amount.toLocaleString("nb-NO")} kr
                        {i === 0 && <span className="ml-1.5 text-[10px] text-green-600 font-normal">høyeste</span>}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground hidden md:table-cell">{formatDateTime(b.createdAt)}</td>
                      <td className="px-6 py-3">
                        <div className="flex justify-end gap-2">
                          {isDeveloper && (
                            <Button variant="outline" size="sm" onClick={() => setSelectedBidAudit(b)}>
                              Audit
                            </Button>
                          )}
                          <button
                            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                            onClick={async () => {
                              if (!confirm(`Slett bud på ${b.amount.toLocaleString("nb-NO")} kr?`)) return;
                              await handleDeleteBid(b);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="hendelser" className="mt-6">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tidspunkt</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hendelse</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Aktør</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden xl:table-cell">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden 2xl:table-cell">Metadata</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.events.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Ingen hendelser ennå</td></tr>
                ) : (
                  data.events.map((event) => (
                    <tr key={event.id}>
                      <td className="px-6 py-3 text-muted-foreground whitespace-nowrap">{formatDateTime(event.createdAt)}</td>
                      <td className="px-6 py-3 min-w-0">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{event.title}</p>
                            <Badge variant="muted" className="text-[10px] uppercase tracking-wide">{event.visibility}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{event.type}</p>
                          <p className="text-xs text-muted-foreground">{event.source}</p>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground hidden lg:table-cell">
                        <div className="space-y-1">
                          <p>{formatEventActor(event)}</p>
                          <p className="text-xs uppercase tracking-wide">{event.actorType}</p>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground hidden xl:table-cell">
                        {event.statusFrom || event.statusTo ? (
                          <span>
                            {event.statusFrom ?? "—"} → {event.statusTo ?? "—"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground hidden 2xl:table-cell">
                        <code className="text-[11px] break-all">{formatEventMetadata(event.metadata)}</code>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ─── VISNINGER ─── */}
        <TabsContent value="visninger" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Visninger
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!viewsLoaded ? (
                <p className="text-sm text-muted-foreground">Laster...</p>
              ) : views.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen visninger registrert.</p>
              ) : (
                <>
                  {(() => {
                    const uniqueUsers = new Set(views.map((v) => v.userId)).size;
                    const uniqueOrgs = new Set(views.filter((v) => v.organizationId).map((v) => v.organizationId)).size;
                    const displayViews = viewsUniqueOnly
                      ? Array.from(
                          views.reduce((map, v) => {
                            if (!map.has(v.userId)) map.set(v.userId, v);
                            return map;
                          }, new Map<string, ViewRow>()).values()
                        )
                      : views;

                    return (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-muted-foreground">
                            {views.length} visning{views.length !== 1 ? "er" : ""} totalt
                            {" · "}
                            {uniqueUsers} unike brukere
                            {" · "}
                            {uniqueOrgs} unike organisasjoner
                          </p>
                          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                            <span className="text-muted-foreground">Kun unike besøkende</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={viewsUniqueOnly}
                              onClick={() => setViewsUniqueOnly(!viewsUniqueOnly)}
                              className="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors"
                              style={{ background: viewsUniqueOnly ? "var(--primary)" : "var(--border)" }}
                            >
                              <span
                                className="pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform"
                                style={{ transform: viewsUniqueOnly ? "translateX(16px)" : "translateX(0)" }}
                              />
                            </button>
                          </label>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                              <th className="pb-2 pr-4">Bruker</th>
                              <th className="pb-2 pr-4">Organisasjon</th>
                              <th className="pb-2 pr-4">Kilde</th>
                              <th className="pb-2">{viewsUniqueOnly ? "Sist sett" : "Tidspunkt"}</th>
                              {viewsUniqueOnly && <th className="pb-2">Antall</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {displayViews.map((v) => (
                              <tr key={v.id} className="border-b last:border-0">
                                <td className="py-2 pr-4">
                                  <div className="font-medium">{v.userName || "Ukjent"}</div>
                                  <div className="text-xs text-muted-foreground">{v.userEmail}</div>
                                </td>
                                <td className="py-2 pr-4">
                                  {v.orgName ? (
                                    <button
                                      onClick={() => navigate(`/organisasjoner/${v.organizationId}`)}
                                      className="text-left hover:underline"
                                    >
                                      <div className="font-medium">{v.orgName}</div>
                                      <div className="text-xs text-muted-foreground">{v.orgSlug}</div>
                                    </button>
                                  ) : (
                                    <span className="text-muted-foreground">–</span>
                                  )}
                                </td>
                                <td className="py-2 pr-4">{v.source}</td>
                                <td className="py-2 whitespace-nowrap">
                                  {new Date(v.viewedAt).toLocaleString("nb-NO", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </td>
                                {viewsUniqueOnly && (
                                  <td className="py-2">
                                    {views.filter((x) => x.userId === v.userId).length}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── REDIGER ─── */}
        {isDeveloper && (
          <TabsContent value="rediger" className="mt-6">
            <div className="space-y-8">
              <AuctionEditForm
                data={data}
                onSave={async (fields) => {
                  const r = await patch("", fields);
                  if (r) setData(d => d ? { ...d, ...r } : d);
                }}
              />
              {data.vehicle ? (
                <VehicleForm
                  vehicle={data.vehicle}
                  onSave={async (fields) => {
                    const r = await patch("/vehicle", fields);
                    if (r) setData(d => d ? { ...d, vehicle: d.vehicle ? { ...d.vehicle, ...r } : null } : d);
                  }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Ingen kjøretøy koblet til denne auksjonen.</p>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <NegotiationModal
        isOpen={negotiationOpen}
        onClose={() => setNegotiationOpen(false)}
        data={data}
        isDeveloper={isDeveloper}
        onUpdate={(fields) => setData(d => d ? { ...d, ...fields } : d)}
        loadAuction={loadAuction}
      />

      {selectedBidAudit && (
        <BidAuditModal 
          bid={selectedBidAudit} 
          onClose={() => setSelectedBidAudit(null)} 
          onDelete={handleDeleteBid}
        />
      )}
    </div>
  );
}

/* ─── Bilder Tab ─── */
function BilderTab({
  images,
  onDelete,
  onReorder,
  onUpload,
  onRerunBlur,
}: {
  images: VehicleImage[];
  onDelete: (id: string) => Promise<void>;
  onReorder: (ordered: VehicleImage[]) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onRerunBlur: (id: string) => Promise<void>;
}) {
  const [blurState, setBlurState] = useState<Record<string, "pending" | "queued">>({});

  async function handleRerunBlur(imgId: string) {
    setBlurState(s => ({ ...s, [imgId]: "pending" }));
    try {
      await onRerunBlur(imgId);
      setBlurState(s => ({ ...s, [imgId]: "queued" }));
    } catch {
      setBlurState(s => {
        const next = { ...s };
        delete next[imgId];
        return next;
      });
    }
  }
  const [items, setItems] = useState<VehicleImage[]>([...images].sort((a, b) => a.sortOrder - b.sortOrder));
  const [lightbox, setLightbox] = useState<VehicleImage | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // Sync when parent images change (e.g. after delete)
  useEffect(() => {
    setItems([...images].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [images]);

  function handleDragStart(i: number) {
    setDragIndex(i);
  }

  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    setOverIndex(i);
  }

  function handleDrop(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }

    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);

    const reindexed = next.map((img, idx) => ({ ...img, sortOrder: idx, isPrimary: idx === 0 }));
    setItems(reindexed);
    setDragIndex(null);
    setOverIndex(null);
    void onReorder(reindexed);
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const uploadButton = (
    <>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" onChange={handleFileSelect} />
      <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
        {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
        {uploading ? "Laster opp…" : "Last opp bilde"}
      </Button>
    </>
  );

  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
        <ImageIcon className="h-10 w-10 opacity-30" />
        <p className="text-sm">Ingen bilder lastet opp</p>
        {uploadButton}
      </div>
    );
  }

  return (
    <>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox.url} alt={lightbox.altText ?? ""} className="max-h-[90vh] max-w-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <XCircle className="h-8 w-8" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">Dra og slipp for å endre rekkefølge. Første bilde brukes som hovedbilde.</p>
        {uploadButton}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((img, i) => (
          <div
            key={img.id}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-grab active:cursor-grabbing transition-all"
            style={{
              opacity: dragIndex === i ? 0.4 : 1,
              outline: overIndex === i && dragIndex !== i ? "2px solid var(--accent)" : "2px solid transparent",
              outlineOffset: 2,
            }}
          >
            <img
              src={img.thumbnailUrl ?? img.url}
              alt={img.altText ?? ""}
              className="w-full h-full object-contain pointer-events-none"
            />
            {i === 0 && (
              <div className="absolute top-2 left-2">
                <Badge variant="accent" className="text-[10px] gap-1 py-0.5"><Star className="h-2.5 w-2.5" />Primær</Badge>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="h-7 w-7 rounded-lg bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                onClick={() => setLightbox(img)}
              >
                <ExternalLink className="h-3.5 w-3.5 text-foreground" />
              </button>
              <button
                className="h-7 w-7 rounded-lg bg-white/90 flex items-center justify-center hover:bg-white transition-colors disabled:opacity-70"
                title={blurState[img.id] === "queued" ? "Sladding satt i kø" : "Kjør skiltsladding på nytt"}
                disabled={blurState[img.id] !== undefined}
                onClick={() => void handleRerunBlur(img.id)}
              >
                {blurState[img.id] === "pending" ? (
                  <Loader2 className="h-3.5 w-3.5 text-foreground animate-spin" />
                ) : blurState[img.id] === "queued" ? (
                  <Check className="h-3.5 w-3.5 text-foreground" />
                ) : (
                  <ScanEye className="h-3.5 w-3.5 text-foreground" />
                )}
              </button>
              <button
                className="h-7 w-7 rounded-lg bg-destructive/90 flex items-center justify-center hover:bg-destructive transition-colors"
                onClick={async () => {
                  if (!confirm("Slett dette bildet?")) return;
                  await onDelete(img.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-white" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Vehicle Form ─── */
function VehicleForm({ vehicle, onSave }: { vehicle: NonNullable<AuctionDetail["vehicle"]>; onSave: (f: object) => Promise<void> }) {
  const [form, setForm] = useState({ ...vehicle });
  const [saving, setSaving] = useState(false);

  const f = (key: keyof typeof form) => ({
    value: (form[key] as string | number | null) ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value === "" ? null : e.target.value })),
  });

  const buildPayload = () => ({
    make: form.make,
    model: form.model,
    variant: form.variant,
    modelYear: form.modelYear,
    mileage: form.mileage,
    registrationNumber: form.registrationNumber,
    vin: form.vin,
    fuelType: form.fuelType,
    transmission: form.transmission,
    bodyType: form.bodyType,
    wheelDrive: form.wheelDrive,
    exteriorColor: form.exteriorColor,
    interiorColor: form.interiorColor,
    conditionGrade: form.conditionGrade,
    conditionSummary: form.conditionSummary,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Grunninfo</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <VField label="Merke" {...f("make")} />
          <VField label="Modell" {...f("model")} />
          <VField label="Variant" {...f("variant")} />
          <VField label="Årsmodell" {...f("modelYear")} type="number" />
          <VField label="Kilometerstand" {...f("mileage")} type="number" />
          <VField label="Reg.nr" {...f("registrationNumber")} />
          <VField label="VIN" {...f("vin")} className="sm:col-span-2" mono />
          <VField label="Tilstandsgrad" {...f("conditionGrade")} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Teknisk</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <VField label="Drivstoff" {...f("fuelType")} />
          <VField label="Girkasse" {...f("transmission")} />
          <VField label="Karosseri" {...f("bodyType")} />
          <VField label="Hjuldrift" {...f("wheelDrive")} />
          <VField label="Eksteriørfarge" {...f("exteriorColor")} />
          <VField label="Interiørfarge" {...f("interiorColor")} />
        </CardContent>
      </Card>
      {form.conditionSummary !== undefined && (
        <Card>
          <CardHeader><CardTitle className="text-sm font-semibold">Tilstandsbeskrivelse</CardTitle></CardHeader>
          <CardContent>
            <textarea
              className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.conditionSummary ?? ""}
              onChange={(e) => setForm(p => ({ ...p, conditionSummary: e.target.value || null }))}
            />
          </CardContent>
        </Card>
      )}
      <div className="flex justify-end">
        <Button disabled={saving} onClick={async () => { setSaving(true); await onSave(buildPayload()); setSaving(false); }} className="gap-2">
          <Pencil className="h-3.5 w-3.5" />{saving ? "Lagrer..." : "Lagre kjøretøy"}
        </Button>
      </div>
    </div>
  );
}

/* ─── Auction Edit Form ─── */
function AuctionEditForm({ data, onSave }: { data: AuctionDetail; onSave: (f: object) => Promise<void> }) {
  const [form, setForm] = useState({
    title: data.title,
    description: data.description ?? "",
    status: data.status,
    organizationId: data.organization.id,
    pickupAddress: data.pickupAddress ?? "",
    pickupPostalCode: data.pickupPostalCode ?? "",
    pickupCity: data.pickupCity ?? "",
    startingBid: data.startingBid,
    reservePrice: data.reservePrice ?? "",
    buyNowPrice: data.buyNowPrice ?? "",
    minimumIncrement: data.minimumIncrement,
    startsAt: data.startsAt ? toDatetimeLocal(data.startsAt) : "",
    endsAt: data.endsAt ? toDatetimeLocal(data.endsAt) : "",
    visibility: data.visibility,
  });
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API}/api/admin/organizations`, { credentials: "include" })
      .then(r => r.json())
      .then((d: { id: string; name: string }[]) => setOrgs(d));
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) setOrgDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()));
  const selectedOrgName = orgs.find(o => o.id === form.organizationId)?.name ?? data.organization.name;

  const statuses = [
    { value: "draft", label: "Utkast" },
    { value: "pending_review", label: "Til godkjenning" },
    { value: "approved", label: "Godkjent" },
    { value: "scheduled", label: "Planlagt" },
    { value: "changes_requested", label: "Endringer ønsket" },
    { value: "live", label: "Live" },
    { value: "paused", label: "Pauset" },
    { value: "pending_seller_decision", label: "Venter på selger" },
    { value: "negotiating", label: "Forhandling" },
    { value: "no_bids", label: "Ingen bud" },
    { value: "sold", label: "Solgt" },
    { value: "no_sale", label: "Ikke solgt" },
    { value: "contract_pending", label: "Kontrakt sendt" },
    { value: "invoice_pending", label: "Faktura utestående" },
    { value: "payment_pending", label: "Avventer betaling" },
    { value: "ready_for_pickup", label: "Klar til å hente" },
    { value: "picked_up", label: "Hentet av transportør" },
    { value: "in_transit", label: "Under transport" },
    { value: "delivered", label: "Levert" },
    { value: "complaint_review", label: "Reklamasjon" },
    { value: "pending_payout", label: "Avventer utbetaling" },
    { value: "paid_out", label: "Utbetalt" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Generelt</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-1">
              <Label className="text-xs text-muted-foreground">Tittel</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1.5" />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs text-muted-foreground">Auksjonsstatus</Label>
              <select
                className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
              >
                {statuses.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="sm:col-span-2" ref={orgRef}>
            <Label className="text-xs text-muted-foreground">Selger (organisasjon)</Label>
            <div className="relative mt-1.5">
              <button
                type="button"
                onClick={() => setOrgDropdownOpen(o => !o)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {selectedOrgName}
              </button>
              {orgDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-background shadow-lg">
                  <div className="p-2">
                    <Input
                      placeholder="Søk organisasjon..."
                      value={orgSearch}
                      onChange={e => setOrgSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredOrgs.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">Ingen treff</div>
                    )}
                    {filteredOrgs.map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => {
                          setForm(p => ({ ...p, organizationId: o.id }));
                          setOrgDropdownOpen(false);
                          setOrgSearch("");
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/40 transition-colors ${o.id === form.organizationId ? "bg-muted/60 font-medium" : ""}`}
                      >
                        {o.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Beskrivelse</Label>
            <textarea
              className="mt-1.5 w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Henteadresse</Label>
            <Input value={form.pickupAddress} onChange={e => setForm(p => ({ ...p, pickupAddress: e.target.value }))} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Postnummer</Label>
              <Input value={form.pickupPostalCode} onChange={e => setForm(p => ({ ...p, pickupPostalCode: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">By</Label>
              <Input value={form.pickupCity} onChange={e => setForm(p => ({ ...p, pickupCity: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Synlighet</Label>
            <div className="flex gap-2 mt-1.5">
              {["private", "public"].map(v => (
                <button key={v} type="button" onClick={() => setForm(p => ({ ...p, visibility: v }))}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${form.visibility === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/40"}`}>
                  {v === "private" ? "Privat" : "Offentlig"}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Priser</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Startpris (kr)</Label>
            <Input type="number" value={form.startingBid} onChange={e => setForm(p => ({ ...p, startingBid: Number(e.target.value) }))} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Minimumsøkning (kr)</Label>
            <Input type="number" value={form.minimumIncrement} onChange={e => setForm(p => ({ ...p, minimumIncrement: Number(e.target.value) }))} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Minstepris (kr, valgfritt)</Label>
            <Input type="number" value={form.reservePrice} onChange={e => setForm(p => ({ ...p, reservePrice: e.target.value }))} className="mt-1.5" placeholder="Ingen" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Kjøp nå-pris (kr, valgfritt)</Label>
            <Input type="number" value={form.buyNowPrice} onChange={e => setForm(p => ({ ...p, buyNowPrice: e.target.value }))} className="mt-1.5" placeholder="Ingen" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Tid</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Starter</Label>
            <Input type="datetime-local" value={form.startsAt} onChange={e => setForm(p => ({ ...p, startsAt: e.target.value }))} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Slutter</Label>
            <Input type="datetime-local" value={form.endsAt} onChange={e => setForm(p => ({ ...p, endsAt: e.target.value }))} className="mt-1.5" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button disabled={saving} onClick={async () => {
          setSaving(true);
          await onSave({
            title: form.title,
            description: form.description || null,
            status: form.status,
            organizationId: form.organizationId,
            pickupAddress: form.pickupAddress || null,
            pickupPostalCode: form.pickupPostalCode || null,
            pickupCity: form.pickupCity || null,
            startingBid: form.startingBid,
            minimumIncrement: form.minimumIncrement,
            reservePrice: form.reservePrice !== "" ? Number(form.reservePrice) : null,
            buyNowPrice: form.buyNowPrice !== "" ? Number(form.buyNowPrice) : null,
            startsAt: form.startsAt || null,
            endsAt: form.endsAt || null,
            visibility: form.visibility,
          });
          setSaving(false);
        }} className="gap-2">
          <Pencil className="h-3.5 w-3.5" />{saving ? "Lagrer..." : "Lagre endringer"}
        </Button>
      </div>
    </div>
  );
}

/* ─── Helpers ─── */
function VField({ label, value, onChange, type = "text", className = "", mono = false }: {
  label: string; value: string | number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string; className?: string; mono?: boolean;
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type={type} value={value} onChange={onChange} className={mono ? "font-mono text-xs" : ""} />
    </div>
  );
}



function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("nb-NO", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function AuditField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="max-w-[60%] break-all text-right">{value}</span>
    </div>
  );
}

function BidAuditModal({ bid, onClose, onDelete }: { bid: BidRow; onClose: () => void; onDelete: (bid: BidRow) => Promise<void> }) {
  const [deleting, setDeleting] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
              Bid Audit
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {bid.amount.toLocaleString("nb-NO")} kr · {formatDateTime(bid.createdAt)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="destructive" 
              size="sm" 
              className="gap-2"
              disabled={deleting}
              onClick={async () => {
                if (!confirm(`Slett bud på ${bid.amount.toLocaleString("nb-NO")} kr?`)) return;
                setDeleting(true);
                await onDelete(bid);
                setDeleting(false);
                onClose();
              }}
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? "Sletter..." : "Slett bud"}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <XCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Identitet</p>
            <div className="space-y-2">
              <AuditField label="Bruker-ID" value={bid.meta?.audit?.bidder?.id ?? bid.bidderUserId} />
              <AuditField label="Navn" value={bid.meta?.audit?.bidder?.name ?? bid.bidderName} />
              <AuditField label="E-post" value={bid.meta?.audit?.bidder?.email ?? bid.bidderEmail} />
              <AuditField label="Telefon" value={bid.meta?.audit?.bidder?.phoneNumber ?? bid.bidderPhoneNumber} />
              <AuditField label="Rolle" value={bid.meta?.audit?.bidder?.role} />
              <AuditField label="Registrert" value={bid.meta?.audit?.bidder?.registeredAt ? formatDateTime(bid.meta.audit.bidder.registeredAt) : null} />
              <AuditField label="E-post verifisert" value={bid.meta?.audit?.bidder?.emailVerified === null || bid.meta?.audit?.bidder?.emailVerified === undefined ? null : bid.meta.audit.bidder.emailVerified ? "Ja" : "Nei"} />
              <AuditField label="Telefon verifisert" value={bid.meta?.audit?.bidder?.phoneNumberVerified === null || bid.meta?.audit?.bidder?.phoneNumberVerified === undefined ? null : bid.meta.audit.bidder.phoneNumberVerified ? "Ja" : "Nei"} />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Organisasjon og bud</p>
            <div className="space-y-2">
              <AuditField label="Organisasjon" value={bid.meta?.audit?.organization?.name ?? bid.bidderOrgName} />
              <AuditField label="Org slug" value={bid.meta?.audit?.organization?.slug ?? bid.bidderOrgSlug} />
              <AuditField label="Org ID" value={bid.meta?.audit?.organization?.id ?? bid.bidderOrganizationId} />
              <AuditField label="Kilde" value={bid.source} />
              <AuditField label="Status" value={bid.status} />
              <AuditField label="Autobud" value={bid.meta?.isAutoBid ? "Ja" : "Nei"} />
              <AuditField label="Trigget av org" value={bid.meta?.triggeredByBidderOrganizationId} />
              <AuditField label="Audit capture" value={bid.meta?.audit?.request?.capturedAt ? formatDateTime(bid.meta.audit.request.capturedAt) : null} />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/20 p-4 lg:col-span-2">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Nettverk og enhet</p>
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              <AuditField label="IP-adresse" value={bid.meta?.audit?.request?.ipAddress} />
              <AuditField label="Lokasjon" value={[bid.meta?.audit?.request?.cfCity, bid.meta?.audit?.request?.cfRegion, bid.meta?.audit?.request?.cfCountry].filter(Boolean).join(", ") || null} />
              <AuditField label="Forwarded for" value={bid.meta?.audit?.request?.forwardedFor} />
              <AuditField label="Forwarded" value={bid.meta?.audit?.request?.forwarded} />
              <AuditField label="Plattform" value={bid.meta?.audit?.request?.secChUaPlatform} />
              <AuditField label="Mobil" value={bid.meta?.audit?.request?.secChUaMobile} />
              <AuditField label="Browser hints" value={bid.meta?.audit?.request?.secChUa} />
              <AuditField label="Språk" value={bid.meta?.audit?.request?.acceptLanguage} />
              <AuditField label="Origin" value={bid.meta?.audit?.request?.origin} />
              <AuditField label="Referer" value={bid.meta?.audit?.request?.referer} />
              <AuditField label="Host" value={bid.meta?.audit?.request?.host} />
              <AuditField label="Fetch site" value={bid.meta?.audit?.request?.secFetchSite} />
              <AuditField label="Fetch mode" value={bid.meta?.audit?.request?.secFetchMode} />
              <AuditField label="Fetch dest" value={bid.meta?.audit?.request?.secFetchDest} />
            </div>
            {bid.meta?.audit?.request?.userAgent && (
              <div className="mt-3 rounded-md bg-white p-3 text-xs break-all">
                <span className="font-semibold text-muted-foreground">User-Agent:</span> {bid.meta.audit.request.userAgent}
              </div>
            )}
            {bid.meta?.audit?.request?.cfLatitude && bid.meta?.audit?.request?.cfLongitude && (
              <div className="mt-3 overflow-hidden rounded-lg border border-border">
                <iframe
                  title="Budgiver lokasjon"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(bid.meta.audit.request.cfLongitude) - 0.05},${Number(bid.meta.audit.request.cfLatitude) - 0.03},${Number(bid.meta.audit.request.cfLongitude) + 0.05},${Number(bid.meta.audit.request.cfLatitude) + 0.03}&layer=mapnik&marker=${bid.meta.audit.request.cfLatitude},${bid.meta.audit.request.cfLongitude}`}
                />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-muted/10 p-4 lg:col-span-2">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Full audit payload</p>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all text-[11px] leading-5 text-foreground">
              {JSON.stringify(bid.meta ?? {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
